import { INIT_DEBUGGER, TRACE } from '../../core/debug/debugMacros';

import { action, computed, makeObservable, observable, observe, reaction } from 'mobx';
import throttle from 'lodash/throttle';
import clamp from 'lodash/clamp';
import debounce from 'lodash/debounce';

import { DualPlusReportStatus, JobSpeciality, LiveStreamActivePlaylist, MomentSource, UpdateJobInput } from '@clipr/lib';
import { JobModel, Moment, MomentModel, MomentStub, Reaction } from '../../entities';
import { Store } from '../../store/store';
import { BindingProps, Message, RefProxy, refProxy, StoreNode } from '../../store';
import { MomentSelector } from '../../entities/moments/momentSelector';
import { IPlayer, IPlayerAdapter, PlayParams, PlayerPrincipalMomentStatus } from './playerSchema';
import { PlayerDirector } from './playerDirector';
import { PlayerMomentView } from './playerMomentView';
import { PlayerReporter } from './playerReporter';
import { MediaInfo } from '../../entities/media';
import { MomentEditorState } from '../momentEditor';
import { EditorWindowMode } from '../editorWindow/editorWindowState';
import { PlayerItemSource } from '../../entities/player/playerItemSource';
import { getPlayerWidgetIFrameCode } from '../../widgets/playerWidget/playerWidgetUtils';
import { notifyError, notifyLoading, notifySuccess } from '../../services/notifications';
import { PlayerFramesetState } from '../playerFrameset/playerFramesetState';
import { BookmarkWindowState } from '../bookmarks/bookmarkWindowState';
import { durationToString } from '../../core/time';
import { MarkerBarState } from './components/markerBarState';
import { AsyncResult, getEventOffset, isFiniteNumber, isNonEmptyString, Maybe, PointerEventLike } from '../../core';
import { PlayerComponentName } from './playerSchema';
import { ApiRequestOptions } from '../../api/apiSchema';
import { InputPlayerReactionName } from '../playerReactions/playerReactionSchema';
import { PlayerReactionAnimationsState } from '../playerReactionAnimations/playerReactionAnimationsState';
import { ShareVideoWindowState, ShareWindowMode, ShareWindowProps } from '../jobs';
import { ShareMeetingAssetsWindowState } from '../jobs';
import { SharedFrom } from '../../services/analytics/stream';
import { MediaStreamMonitor } from '../../entities/media/mediaStreamMonitor';
import { PlayerTutorialWindowState } from '../playerTutorial/playerTutorialWindowState';
import { ComponentVisibility, IComponentPolicy } from '../componentSchema';
import { AdsBarState } from './components/adsBarState';
import { randomArrayElement } from '../../core/random';
import { PlayerAdapterState } from '../playerAdapter';
import { PlayerAdsAdapterState } from '../playerAdsAdapter';
import { PlayerLiveAdapter } from './playerLiveAdapter';
import { PlayheadState } from './playheadState';
import { PlayerChromeState } from './playerChromeState';
import { PlayerCaptionState } from './playerCaptionsState';
import { PlayerProgressBarState } from './playerProgressBarState';
import { PlayerVolumeButtonState } from './playerVolumeButtonState';
import { PlayerReactionButtonState } from './playerReactionButtonState';
import { PlayerAdsAdapterMessageType } from '../playerAdsAdapter/playerAdsAdapterSchema';
import { Config } from '../../config';
import { ConfigAutoplayPolicy } from '../../config/configSchema';
import { OpenClipWindowOptions } from '../momentWindow/clipWindowState';
import {
  isMomentDirectorEnabled,
  shouldShowAddClip,
  getMomentStub,
  momentHasBookmark as momentHasBookmarkHelper,
  getJumpToMomentTime,
  getJumpToCommentTime,
  getJumpToReactionTime,
  getJumpToCommentOrReactionTime,
  getDirectorInitialSeekTime,
} from './playerMomentsState';
import {
  isLiveStreamRunning as computeIsLiveStreamRunning,
  isRealTime as computeIsRealTime,
  isSeekable as computeIsSeekable,
  showLiveWaitingScreen as computeShowLiveWaitingScreen,
  showLiveNotStreamingScreen as computeShowLiveNotStreamingScreen,
  showLiveEndedScreen as computeShowLiveEndedScreen,
  getLiveOffsetLabel,
  showLiveReactions as computeShowLiveReactions,
  isLivePlaybackActive as computeIsLivePlaybackActive,
  isMonitorEnabled as computeIsMonitorEnabled,
  getCurrentAbsoluteTimeByDate,
  getCurrentAbsoluteTimeBySN,
  getCurrentAbsoluteTime,
  getReactionTime,
} from './playerLiveState';
import {
  shouldShowReactionsButton,
  addReaction as addReactionHelper,
} from './playerReactionsState';
import {
  isTutorialOnForChromeTop,
  isTutorialOnForReactions,
  getOrientationAction,
  handleTutorialWindowMessage,
} from './playerWidgetState';

type Props = {
  adapter?: PlayerAdapterState | null;
} & BindingProps<{
  jobId: string;
  momentSelector: MomentSelector;
  frameset?: PlayerFramesetState | null;
  widgetMode?: boolean | null;
  speakerIdMode?: boolean | null;
  showCommentsBar?: boolean | null;
  suppressLiveReactions?: boolean | null;
  time?: number;
  teamId?: string | null;
  showIndex?: boolean,
  showComments?: boolean,
  showTranscript?: boolean,
  showProfile?: boolean,
  showTopicTags?: boolean,
  showEmbedIcon?: boolean,
  media?: MediaInfo,
  showReactionButton?: boolean;
  showAddMomentButton?: boolean;
  allowFullscreen?: boolean | null;
  playlistType?: LiveStreamActivePlaylist;
  allowShare?: boolean | null;
  allowDownload?: boolean | null;
  showHelp?: boolean | null;
  disableReactions?: boolean | null;
  disableComments?: boolean | null;
  disableTranscript?: boolean | null;
  disableIndex?: boolean | null;
  customRedirectUrl?: string | null;
  autoplay?: boolean | null;
  playerSource?: string | null;
  playerTutorialHighlightedComponents?: string[];
  openMomentWindowOptions?: Partial<OpenClipWindowOptions>;
  momentSource?: MomentSource;
}>

type PlaybackRateOption = {
  label: string,
  rate: number,
  iconLabel: string
}

type PlayerStatus =
  'Idle' |
  'Playing' |
  'Waiting' |
  'Seeking' |
  'Paused' |
  'Ended' |
  'Stopped';

interface PlayerInvokePayload {
  time?: number;
  rate?: number;
  volume?: number;
  jumpToLive?: boolean;
  autoplay?: boolean;
  moment?: Moment;
  setAsPrincipal?: boolean;
  comment?: { videoTime?: number | null };
  reaction?: { videoTime?: number | null };
  target?: { videoTime?: number | null; isLive?: boolean };
}

const SEEK_DEBOUNCE_WAIT = 200;
const ADD_REACTION_THROTTLE_TIME = 300;

export class PlayerState
  extends StoreNode
  implements IPlayer {

  readonly nodeType = 'PlayerState';

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'momentDirectorMoments' | 'momentDirectorEnabled' | 'momentReporterEnabled' | 'setMounted' | 'setUnmounted' | 'playerTutorialWindowListener'>(this, {
      tutorialMode: observable,
      selector: computed,
      playlistType: computed,
      playerSource: computed,
      jobId: computed,
      time: computed,
      frameset: computed,
      widgetMode: computed,
      speakerIdMode: computed,
      showCommentsBar: computed,
      showIndex: computed,
      showComments: computed,
      showTranscript: computed,
      disableComments: computed,
      disableTranscript: computed,
      disableIndex: computed,
      showProfile: computed,
      showTopicTags: computed,
      showEmbedIcon: computed,
      showReactionButton: computed,
      showAddMomentButton: computed,
      showShareButton: computed,
      allowFullscreen: computed,
      allowShare: computed,
      allowDownload: computed,
      showHelp: computed,
      disableReactions: computed,
      autoplay: computed,
      customRedirectUrl: computed,
      suppressLiveReactions: computed,
      openMomentWindowOptions: computed,
      momentSource: computed,
      isMounted: observable,
      lockOrientation: observable,
      isUiPinned: observable,
      principalMoment: observable,
      principalMomentPlaybackStatus: computed,
      activeAdapter: computed,
      playbackRateOptions: computed,
      lastUserAction: observable,
      showLastActionFeedback: observable,
      isLiveStream: computed,
      isLiveStreamRunning: computed,
      isRealTime: computed,
      isSeekable: computed,
      isPlaying: computed,
      duration: computed,
      hasDuration: computed,
      currentTime: computed,
      bufferedTime: computed,
      captionsAreEnabled: computed,
      captionsAreVisible: computed,
      captionText: computed,
      isPaused: computed,
      currentTimeRatio: computed,
      bufferedTimeRatio: computed,
      volume: computed,
      playbackRate: computed,
      playbackRateItem: computed,
      isSeeking: computed,
      isWaiting: computed,
      isAudioSource: computed,
      teamId: computed,
      isManualReportOnly: computed,
      playCount: observable,
      playInvokeCount: observable,
      isStopped: computed,
      isEnded: observable,
      isMuted: observable,
      volumeBeforeMute: observable,
      isReady: observable,
      isReplay: observable,
      isSeekRequested: observable,
      isSeekDragging: observable,
      currentSeekTime: observable,
      wasPlayingBeforeEnterPause: observable,
      isPauseEntered: observable,
      isHoverSeeking: observable,
      hoverSeekTime: observable,
      status: computed,
      displayStatus: computed,
      showPoster: computed,
      currentSeekTimeRatio: computed,
      hoverSeekTimeRatio: computed,
      playheadTime: computed,
      playheadTimeRatio: computed,
      isAdsAdapterActive: computed,
      isAdPlaybackMode: computed,
      isMobile: computed,
      activeCaptionItem: computed,
      hasMultipleCaptionTracks: computed,
      activeAudioTrack: computed,
      hasMultipleAudioTracks: computed,
      showReactionsButton: computed,
      showAddClip: computed,
      isEnrichmentProcessing: computed,
      isTranscriptProcessing: computed,
      momentStub: computed,
      showLiveWaitingScreen: computed,
      showLiveNotStreamingScreen: computed,
      showLiveEndedScreen: computed,
      isWidgetMode: computed,
      playerTutorialIsOnForChromeTop: computed,
      playerTutorialIsOnForReactions: computed,
      job: computed,
      media: computed,
      momentDirectorMoments: computed,
      momentDirectorEnabled: computed,
      momentReporterEnabled: computed,
      isFullscreen: computed,
      currentTimeLabel: computed,
      durationLabel: computed,
      liveOffsetLabel: computed,
      showLiveReactions: computed,
      isLivePlaybackActive: computed,
      isMonitorEnabled: computed,
      currentAbsoluteTimeByDate: computed,
      currentAbsoluteTimeBySN: computed,
      currentAbsoluteTime: computed,
      reactionTime: computed,
      isEditMode: computed,
      mounted: action,
      registerListeners: action,
      openEditorWindow: action,
      isGeneratingDualPlusReport: observable,
      setDualPlusReportGenerating: action,
      openDualPlusPublicSafetyReport: action,
      openPublicSafetyJobEditorWindow: action,
      updateJob: action,
      unregisterListeners: action,
      unmounted: action,
      reset: action,
      resetPlayState: action,
      setMounted: action,
      setUnmounted: action,
      invokeHandler: action,
      invoke: action,
      handleFullscreenChange: action,
      enterEditMode: action,
      exitEditMode: action,
      toggleEditMode: action,
      openBookmarkWindow: action,
      applyDirectorInitialSeek: action,
      applyReplayInitialSeek: action,
      shareVideo: action,
      handleOrientationChange: action,
      playerTutorialWindowListener: action,
      seek: action,
      seekStep: action,
      seekStepForward: action,
      seekStepBackward: action,
      adapterSeek: action,
      enterRealTime: action,
      exitRealTime: action,
      updateRealTime: action,
      playheadListener: action,
      progressBarListener: action,
      handleLivePlaybackCompleted: action,
      adapterListener: action,
      adsAdapterListener: action,
      playerLiveAdapterListener: action,
      handleVolumeSliderChange: action,
      applyTimeParam: action,
      setAdTagUrl: action,
      loadAdTagUrl: action,
    });

    INIT_DEBUGGER(this, {
      color: 'maroon',
      enableTrace: false
    });

    this.adapter = props?.adapter ?? new PlayerAdapterState(store, {
      media: () => this.media,
      jobId: () => this.jobId
    });
    this.adapter.listen(
      this.adapterListener);

    this.adsAdapter = new PlayerAdsAdapterState(this.store, {
      contentVideoElement: () => this.adapter.videoElement
    });
    this.adsAdapter.listen(
      this.adsAdapterListener);

    this.playhead = new PlayheadState(store, {
      player: () => this,
      containerRef: () => this.progressBarRef
    });
    this.playhead.listen(
      this.playheadListener);

    this.progressBar = new PlayerProgressBarState(store, {
      player: () => this,
      containerRef: () => this.progressBarRef
    });
    this.progressBar.listen(
      this.progressBarListener);

    this.playerLiveAdapter = new PlayerLiveAdapter(store, {
      jobId: () => this.jobId,
      isActive: () => this.isLiveStream,
      adapter: () => this.adapter
    });

    this.playerLiveAdapter.listen(
      this.playerLiveAdapterListener);

    this.itemSource = new PlayerItemSource(store, {
      jobId: () => this.jobId,
      selector: () => this.selector
    });

    this.momentView = new PlayerMomentView(store, {
      player: () => this,
      source: () => this.itemSource
    });

    this.momentDirector = new PlayerDirector(store, {
      player: () => this,
      moments: () => this.momentDirectorMoments,
      enabled: () => this.momentDirectorEnabled,
      passageLength: 0.0001
    });

    this.momentReporter = new PlayerReporter(store, {
      player: () => this,
      enabled: () => this.momentReporterEnabled,
      moments: () => []
    });

    this.momentEditor = new MomentEditorState(this.store, {
      player: () => this,
      jobId: () => this.jobId,
      source: () => this.momentSource
    });

    this.commentsBar = new MarkerBarState(this.store, {
      jobId: () => this.jobId,
      player: () => this,
    });

    this.adsBar = new AdsBarState(this.store, {
      player: () => this
    });

    this.mediaStreamMonitor = new MediaStreamMonitor(this.store, {
      streamURL: () => this.job?.media.liveStreamUrl,
      enabled: () => this.isMonitorEnabled
    });

    // TODO: refactor these observe calls to MobX reactions
    this.mobxDisposers.push(
      observe(this.momentView, 'activeTopic', () => {
        this.emit('activeTopicChange', { moment: this.momentView.activeTopic });
      })
    );

    this.mobxDisposers.push(
      observe(this.momentView, 'lastActiveSubtopic', () => {
        this.emit('lastActiveSubtopicChange', { moment: this.momentView.lastActiveSubtopic });
      })
    );

    this.mobxDisposers.push(
      observe(this.momentView, 'activeTranscript', () => {
        this.emit('activeTranscriptChange', { moment: this.momentView.activeTranscript });
      })
    );

    this.mobxDisposers.push(
      observe(this.momentView, 'activeComment', () => {
        this.emit('activeCommentChange', { comment: this.momentView.activeComment });
      })
    );

    this.mediaStreamMonitor.listen(
      this.mediaStreamMonitorListener);

    this.mobxDisposers.push(
      reaction(() => this.isFullscreen, () =>
        this.handleFullscreenChange())
    );

    this.mobxDisposers.push(
      reaction(() => this.momentView.activeTranscriptSpeakerId, () => {
        this.emit('activeSpeakerChange', { speaker: this.momentView.activeTranscriptSpeaker })
      })
    );

    this.mobxDisposers.push(
      reaction(() => this.principalMomentPlaybackStatus, (curr, prev) =>
        this.handlePrincipalMomentStatusReaction(curr, prev))
    );
  }

  private mediaStreamMonitorListener = async (msg: Message<MediaStreamMonitor>) => {
    const { type } = msg;

    switch (type) {
      case 'active':
        // if (!this.adapter.isMetadataLoaded)
        //   this.adapter.reattachDOMVideo();
        break;
      case 'inactive':
        this.playerLiveAdapter.setStreamEnded();
        this.mediaStreamMonitor.reset(); // if the stream is ended, the monitor should stop
        break;
    }
  }

  private mobxDisposers: (() => void)[] = [];

  tutorialMode: boolean = false;

  readonly itemSource: PlayerItemSource;
  readonly momentView: PlayerMomentView;
  readonly momentDirector: PlayerDirector;
  readonly momentReporter: PlayerReporter;
  readonly momentEditor: MomentEditorState;
  readonly commentsBar: MarkerBarState;
  readonly adsBar: AdsBarState;

  readonly componentRef = refProxy<HTMLDivElement>(this);
  readonly progressBarRef = refProxy<HTMLDivElement>(this);

  readonly reactionAnimations = new PlayerReactionAnimationsState(this.store);

  readonly mediaStreamMonitor: MediaStreamMonitor;

  readonly adapter: PlayerAdapterState;
  readonly adsAdapter: PlayerAdsAdapterState;
  readonly playerLiveAdapter: PlayerLiveAdapter;
  readonly playhead: PlayheadState;

  // #region Props
  // -------
  get selector(): MomentSelector | null {
    return this.resolvedProps.momentSelector ?? null;
  }

  get playlistType(): LiveStreamActivePlaylist | null {
    return this.resolvedProps.playlistType ?? null;
  }

  get playerSource(): string | null {
    return this.resolvedProps.playerSource ?? null;
  }

  get jobId(): string | null {
    return this.resolvedProps.jobId ?? null;
  }

  get time(): number {
    return this.resolvedProps.time ?? 0;
  }

  get frameset(): PlayerFramesetState | null {
    return this.resolvedProps.frameset ?? null;
  }

  get widgetMode(): boolean | null {
    return this.resolvedProps.widgetMode ?? null;
  }
  get speakerIdMode(): boolean | null {
    return this.resolvedProps.speakerIdMode ?? null;
  }

  get showCommentsBar(): boolean {
    return this.resolvedProps.showCommentsBar ?? false;
  }

  get showIndex(): boolean {
    return this.resolvedProps.showIndex ?? true;
  }

  get showComments(): boolean {
    return this.resolvedProps.showComments ?? true;
  }

  get showTranscript(): boolean {
    return this.resolvedProps.showTranscript ?? true;
  }

  get disableComments(): boolean {
    return this.resolvedProps.disableComments ?? false;
  }

  get disableTranscript(): boolean {
    return this.resolvedProps.disableTranscript ?? false;
  }

  get disableIndex(): boolean {
    return this.resolvedProps.disableIndex ?? false;
  }

  get showProfile(): boolean {
    return this.resolvedProps.showProfile ?? true;
  }

  get showTopicTags(): boolean {
    return this.resolvedProps.showTopicTags ?? true;
  }
  get showEmbedIcon(): boolean {
    return this.resolvedProps.showEmbedIcon ?? true;
  }

  get showReactionButton(): boolean {
    return this.resolvedProps.showReactionButton ?? true;
  }

  get showAddMomentButton(): boolean | null {
    return this.resolvedProps.showAddMomentButton ?? null;
  }

  get showShareButton(): boolean {
    return this.resolvedProps.showShareButton ?? false;
  }

  get allowFullscreen(): boolean | null {
    return (this.resolvedProps.allowFullscreen && !this.store.ui.isIOS) ?? null;
  }

  get allowShare(): boolean | null {
    return this.resolvedProps.allowShare ?? null;
  }

  get allowDownload(): boolean | null {
    return this.resolvedProps.allowDownload ?? null;
  }

  get showHelp(): boolean | null {
    return this.resolvedProps.showHelp ?? null;
  }

  get disableReactions(): boolean | null {
    return this.resolvedProps.disableReactions ?? null;
  }

  get autoplay(): boolean | null {
    return this.resolvedProps.autoplay ?? null;
  }

  get customRedirectUrl(): string | null {
    return this.resolvedProps.customRedirectUrl ?? null;
  }

  get suppressLiveReactions(): boolean {
    return this.resolvedProps.suppressLiveReactions ?? false;
  }

  get openMomentWindowOptions(): Partial<OpenClipWindowOptions> | null {
    return this.resolvedProps.openMomentWindowOptions ?? null;
  }

  get momentSource(): Partial<MomentSource> | null {
    return this.resolvedProps.momentSource ?? null;
  }
  // #endregion

  isMounted: boolean = false;
  lockOrientation: boolean = false;
  isUiPinned = false;

  /**
   * Reference to the `Moment` which was opened explicitly in the player,
   * from a bookmark, shared topic / subtopic, etc.
   * The player needs to pause when this moment ends.
   */
  principalMoment: Moment | null = null;

  /**
   * Returns a `PlayerPrincipalMomentStatus` value indicating the relationship
   * between the current `principalMoment` and the player current time.
   * @see `PlayerPrincipalMomentStatus` docs for  for more details.
   */
  get principalMomentPlaybackStatus(): PlayerPrincipalMomentStatus {
    const mom = this.principalMoment;
    const time = this.currentTime;

    if (!mom)
      return PlayerPrincipalMomentStatus.MomentNotSet;

    if (time < mom.startTime)
      return PlayerPrincipalMomentStatus.PlaybackNotStarted;

    if (time > mom.endTime)
      return PlayerPrincipalMomentStatus.PlaybackEnded;

    return PlayerPrincipalMomentStatus.CurrentlyPlaying;
  }

  get activeAdapter(): IPlayerAdapter {
    if (this.adsAdapter.isActive)
      return this.adsAdapter;
    return this.adapter;
  }

  get playbackRateOptions(): PlaybackRateOption[] {
    return (this.isLiveStream && this.isRealTime) ?
      [
        { label: '0.5', rate: 0.5, iconLabel: '0.5x' },
        { label: '0.75', rate: 0.75, iconLabel: '0.75x' },
        { label: 'Normal', rate: 1.0, iconLabel: '1x' },
      ] :
      [
        { label: '0.5', rate: 0.5, iconLabel: '0.5x' },
        { label: '0.75', rate: 0.75, iconLabel: '0.75x' },
        { label: 'Normal', rate: 1.0, iconLabel: '1x' },
        { label: '1.25', rate: 1.25, iconLabel: '1.25x' },
        { label: '1.5', rate: 1.5, iconLabel: '1.5x' },
        { label: '1.75', rate: 1.75, iconLabel: '1.75x' },
        { label: '2', rate: 2.0, iconLabel: '2x' }
      ];
  }

  readonly chrome = new PlayerChromeState(this.store, {
    controller: () => this
  });

  readonly playerCaption = new PlayerCaptionState(this.store, {
    adapter: () => this.adapter
  });

  readonly progressBar: PlayerProgressBarState;

  readonly volumeButton = new PlayerVolumeButtonState(this.store, {
    adapter: () => this.activeAdapter
  });

  readonly reactionButton = new PlayerReactionButtonState(this.store, {

  });

  lastUserAction: string | null = null;
  showLastActionFeedback: boolean = false;

  get isLiveStream(): boolean {
    return !!this.adapter?.isLiveStream;
  }

  get isLiveStreamRunning(): boolean {
    return computeIsLiveStreamRunning(this);
  }

  get isRealTime() { return computeIsRealTime(this); }
  get isSeekable() { return computeIsSeekable(this); }

  get isPlaying() { return this.activeAdapter.isPlaying; }
  get duration() {
    if (this.isLiveStream)
      return this.playerLiveAdapter.duration;

    return this.activeAdapter.duration;
  }
  get hasDuration() { return this.activeAdapter.hasDuration; }
  get currentTime() {
    if (this.isLiveStream)
      return this.playerLiveAdapter.currentTime;

    return this.activeAdapter.currentTime;
  }
  get bufferedTime() {
    if (this.isLiveStream)
      return this.playerLiveAdapter.bufferedTime;

    return this.activeAdapter.bufferedTime;
  }
  get captionsAreEnabled() { return this.playerCaption.enabled; }
  get captionsAreVisible() { return this.playerCaption.showCaptions; }
  get captionText() { return this.playerCaption.captionText; }

  get isPaused() { return !this.isPlaying; }

  get currentTimeRatio() {
    if (this.isLiveStream)
      return this.playerLiveAdapter.currentTimeRatio;

    if (this.hasDuration)
      return (this.currentTime / this.duration) || 0;
    return 0;
  }

  get bufferedTimeRatio() {
    if (this.isLiveStream)
      return this.playerLiveAdapter.bufferedTimeRatio;

    if (this.hasDuration)
      return (this.bufferedTime / this.duration) || 0;

    return 0;
  }

  get volume() {
    return this.activeAdapter.volume;
  }

  get playbackRate() {
    return this.activeAdapter.playbackRate;
  }
  get playbackRateItem(): PlaybackRateOption | null {
    return this.playbackRateOptions.find(opt => opt.rate === this.playbackRate) ?? null;
  }

  get isSeeking(): boolean {
    return this.activeAdapter.isSeeking;
  }

  get isWaiting(): boolean {
    return this.activeAdapter.isWaiting;
  }

  get isAudioSource(): boolean {
    return this.media?.basic ? true : false;
  }

  get teamId(): string | null {
    return this.resolvedProps.teamId ?? null;
  }

  // Keyed on the job, not the team default: the team default only seeds the upload
  // form, and changing it must not retroactively alter how already-processed jobs
  // render. job.speciality is what the report was actually generated as.
  get isManualReportOnly(): boolean {
    return this.job?.speciality === JobSpeciality.PatrolAiSuggestions;
  }

  /** 
   * The number of times the `play` event has been emitted by the adapter since the last reset.
   * According to the docs, the play event is fired when the `paused` property is changed from `true` to `false`, 
   * as a result of the `play` method, or the `autoplay` attribute.
   * This field doesn't track the number of times the `play` method has been invoked on this controller.
   * Use `playInvokeCount` for that.
   */
  playCount: number = 0;

  /**
   * The number of times the `play` method has been invoked on this controller since the last reset.
   * This field doesn't track the number of times the `play` event has been emitted by the adapter.
   * Use `playCount` for that.
   */
  playInvokeCount: number = 0;

  get isStopped(): boolean {
    return (
      // no play request has been made and autoplay didn't kick in
      (this.playCount === 0 && this.playInvokeCount === 0 && !this.adsAdapter.isStarted) ||
      this.isEnded);
  }

  isEnded = false;
  isMuted = false;
  volumeBeforeMute: number | null = null;
  isReady = false;

  isReplay = false;

  /**
   * Indicates if a seek operation has been requested to the player.
   * Useful when seek dragging has been debounced, but you want to keep the playhead in the last seeked position,
   * which will differ from player's currentTime until the seek is completed.
   */
  isSeekRequested = false;

  /** True while the user is dragging the playhead in any of the sections where a playhead is available. */
  isSeekDragging = false;

  /** UI only state representing the current seek position while the user is dragging the playhead or while seeking has been requested to the player. */
  currentSeekTime: number | null = null;

  wasPlayingBeforeEnterPause = false;
  isPauseEntered = false;

  // Hover seek
  isHoverSeeking = false;
  hoverSeekTime = 0;

  get status(): PlayerStatus {
    if (this.isWaiting)
      return 'Waiting';
    if (this.isSeeking)
      return 'Seeking';
    if (this.isPlaying)
      return 'Playing';
    if (this.isEnded)
      return 'Ended';
    if (this.isStopped)
      return 'Stopped';
    if (this.isPaused)
      return 'Paused';

    return 'Idle';
  }

  get displayStatus(): PlayerStatus {
    if (this.isWaiting)
      return 'Waiting';
    if (this.isPlaying)
      return 'Playing';
    if (this.isEnded)
      return 'Ended';
    if (this.isStopped)
      return 'Stopped';
    if (this.isPaused)
      return 'Paused';
    if (this.isSeeking)
      return 'Seeking';

    return 'Idle';
  }

  /**
   * If true it means the video is stopped or ended and the player 
   * should display a poster image, if available.
   */
  get showPoster(): boolean {
    if (this.isAdsAdapterActive)
      return false;

    if (this.speakerIdMode)
      return this.isAudioSource;

    return (
      this.status === 'Stopped' ||
      this.status === 'Ended' ||
      this.isAudioSource);
  }

  get currentSeekTimeRatio(): number {

    if (this.currentSeekTime === null)
      return 0;

    if (this.isLiveStream)
      return this.playerLiveAdapter.getTimeRatio(this.currentSeekTime);

    if (this.hasDuration)
      return (this.currentSeekTime / this.duration) || 0;

    return 0;
  }

  get hoverSeekTimeRatio(): number {

    if (this.isLiveStream)
      return this.playerLiveAdapter.getTimeRatio(this.hoverSeekTime);

    if (this.hasDuration)
      return (this.hoverSeekTime / this.duration) || 0;
    return 0;
  }

  /**
   * Gets the display position of the playhead, depending on the current seeking (dragging) state,
   * and also clamps it in [0, 1].
   */
  get playheadTime(): number {

    let position;
    if (this.isSeekDragging || this.isSeekRequested) {
      if (this.currentSeekTime === null)
        return 0;

      position = this.currentSeekTime;
    } else {
      position = this.currentTime;
    }

    return clamp(position, 0, this.duration);
  }

  get playheadTimeRatio(): number {

    if (this.isRealTime)
      return 1;

    if (this.isLiveStream)
      return this.playerLiveAdapter.playheadTimeRatio(this.playheadTime);

    if (this.hasDuration)
      return (this.playheadTime / this.duration) || 0;
    return 0;
  }

  get isAdsAdapterActive() {
    return this.adsAdapter.isActive;
  }

  get isAdPlaybackMode() {
    return this.isAdsAdapterActive && this.adsAdapter.hasAd;
  }

  get isMobile() {
    return !!this.store.ui.isMobile;
  }

  get activeCaptionItem() {
    return this.playerCaption.activeCaptionTrack;
  }

  get hasMultipleCaptionTracks(): boolean {
    return this.playerCaption.hasMultipleCaptionTracks;
  }

  get activeAudioTrack() {
    return this.adapter.playerAudioTracks.activeAudioTrack;
  }

  get hasMultipleAudioTracks(): boolean {
    return this.adapter.playerAudioTracks.hasMultipleAudioTracks;
  }

  get showReactionsButton(): boolean {
    return shouldShowReactionsButton(this);
  }

  get showAddClip(): boolean {
    return shouldShowAddClip(this);
  }

  get isEnrichmentProcessing(): boolean {
    return (this.job?.isEnrichmentProcessing || this.job?.isEnrichmentPending) ?? false;
  }

  get isTranscriptProcessing(): boolean {
    return (this.job?.isTranscriptProcessing || this.job?.isTranscriptPending) ?? false;
  }

  get momentStub(): MomentStub | null {
    return getMomentStub(this);
  }

  get showLiveWaitingScreen(): boolean {
    return computeShowLiveWaitingScreen(this);
  }

  get showLiveNotStreamingScreen(): boolean {
    return computeShowLiveNotStreamingScreen(this, this.playlistType);
  }

  get showLiveEndedScreen(): boolean {
    return computeShowLiveEndedScreen(this);
  }

  get isWidgetMode(): boolean {
    return this.store.widgetService.getIsWidgetMode(this.widgetMode);
  }

  // TODO: fix OverlayState so these props can be removed
  get playerTutorialIsOnForChromeTop(): boolean {
    return isTutorialOnForChromeTop(this.resolvedProps.playerTutorialHighlightedComponents);
  }

  get playerTutorialIsOnForReactions(): boolean {
    return isTutorialOnForReactions(this.resolvedProps.playerTutorialHighlightedComponents);
  }

  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  get media(): MediaInfo | null {
    return this.resolvedProps.media || this.job?.media || null;
  }

  private get momentDirectorMoments(): MomentModel[] {
    return this.itemSource.directorMoments;
  }

  private get momentDirectorEnabled(): boolean {
    return isMomentDirectorEnabled(this.selector);
  }

  private get momentReporterEnabled(): boolean {
    return true;
  }

  get isFullscreen() {
    return this.store.fullscreen.fullscreenElement === this.componentRef.node;
  }

  get currentTimeLabel(): string {

    let time;
    if (this.isSeekDragging || this.isSeekRequested) {
      time = this.currentSeekTime ?? 0;
    } else
      time = this.currentTime;

    return durationToString(time, 'time');
  }

  get durationLabel(): string {
    return durationToString(this.duration, 'time');
  }

  get liveOffsetLabel(): string {
    return getLiveOffsetLabel(this);
  }

  get showLiveReactions() { return computeShowLiveReactions(this); }

  // is current live stream connected and playable - media derived
  get isLivePlaybackActive(): boolean {
    return computeIsLivePlaybackActive(this);
  }

  get isMonitorEnabled(): boolean {
    return computeIsMonitorEnabled(this);
  }

  get currentAbsoluteTimeByDate(): number | null {
    return getCurrentAbsoluteTimeByDate(this);
  }

  get currentAbsoluteTimeBySN(): number | null {
    return getCurrentAbsoluteTimeBySN(this);
  }

  get currentAbsoluteTime(): number | null {
    return getCurrentAbsoluteTime(this);
  }

  get reactionTime(): number | null {
    return getReactionTime(this);
  }

  get isEditMode() {
    return this.momentEditor.isEditMode;
  }

  async mounted() {
    TRACE(this, `mounted()`);

    this.emit('Mounted');
    this.setMounted();
    this.registerListeners();
  }

  registerListeners() {
    this.store.playerTutorialWindow.listen(this.playerTutorialWindowListener);
    this.store.orientation.register(this.handleOrientationChange);
  }

  openEditorWindow = async (fileName: string, mode: EditorWindowMode) => {
    this.invoke('enterPause');

    if (mode === 'translation' && this.jobId) {
      const [res] = await this.store.apiService.getTranslatedDocument({ jobId: this.jobId });
      if (res?.getTranslatedDocument?.url) {
        const response = await fetch(res.getTranslatedDocument.url);
        const data = await response.text();
        this.dispatch('openEditorWindow', { data, fileName, jobId: this.jobId, mode, editorMode: 'collaboration' });
      }
      return;
    }

    // Always fetch fresh data before opening the editor so it
    // is initialized with the latest S3 content, not a cached store value.
    if (this.jobId) {
      await this.store.jobManager.apiFetchJob(this.jobId);
    }

    const teamId = this.teamId ?? this.job?.ownerTeamId;
    const team = teamId ? this.store.teamManager.getTeam(teamId) : null;
    // Which document to open follows the job's own speciality. generativeAiProvider
    // stays team-scoped -- it is a team capability, not a per-job property.
    const patrolAiSuggestions = mode === 'report' && this.job?.speciality === JobSpeciality.PatrolAiSuggestions;
    const suggestionsEnabled = patrolAiSuggestions && team?.generativeAiProvider === 'Amazon';

    const dataByMode: Record<string, string | null | undefined> = {
      notes: this.job?.publicSafetyNotes,
      report: patrolAiSuggestions
        ? this.job?.publicSafetyManualReport
        : this.job?.publicSafetyReport,
      customerFollowUpLetter: this.job?.publicSafetyCustomerFollowUpLetter,
      dualPlusReport: this.job?.publicSafetyDualPlusReport,
    };

    this.dispatch('openEditorWindow', {
      data: dataByMode[mode],
      fileName,
      jobId: this.jobId,
      mode,
      isManualReport: patrolAiSuggestions,
      aiDraftContent: suggestionsEnabled ? this.job?.publicSafetyReport : undefined,
      editorMode: suggestionsEnabled ? 'suggestions' : 'collaboration',
    });
  };

  // Default 5s; overridable to 0 in tests so the poll loop does not use a real timer.
  dualPlusPollIntervalMs = 5000;
  isGeneratingDualPlusReport = false;

  setDualPlusReportGenerating(value: boolean) {
    this.isGeneratingDualPlusReport = value;
  }

  openDualPlusPublicSafetyReport = async () => {
    if (!this.jobId) return;

    this.invoke('enterPause');
    await this.store.jobManager.apiFetchJob(this.jobId);

    // Already generated — open it without touching the async pipeline.
    if (this.job?.publicSafetyDualPlusReport) {
      this.dispatchDualPlusEditor(this.job);
      return;
    }

    // Generating needs edit access; the backend refuses it and a retry never helps.
    if (!this.job?.hasPermission('UserEditJob')) {
      notifyError(this, 'The Dual+ report has not been generated yet, and you do not have permission to generate it.');
      return;
    }

    // Generation runs asynchronously (Step Functions + worker Lambda). Kick it off
    // unless one is already in flight, then poll the narrow status until it settles.
    this.setDualPlusReportGenerating(true);
    try {
      let status = await this.fetchDualPlusStatus();
      if (status !== DualPlusReportStatus.Generating) {
        notifyLoading(this, 'Generating Dual+ report...');
        const [, err] = await this.store.jobManager.apiGenerateDualPlusPublicSafetyReport({ jobId: this.jobId });
        if (err) {
          // A concurrent run (reload, second tab, partner) already holds the claim, so the
          // mutation rejects. Re-read the status: if something is genuinely generating we
          // join the poll instead of erroring; anything else is a real failure.
          status = await this.fetchDualPlusStatus();
          if (status !== DualPlusReportStatus.Generating) {
            notifyError(this, 'Could not generate the Dual+ report. Try again later.');
            return;
          }
        }
      } else {
        notifyLoading(this, 'Generating Dual+ report...');
      }

      const finalStatus = await this.pollDualPlusStatus();
      if (finalStatus !== DualPlusReportStatus.Complete) {
        notifyError(this, 'Could not generate the Dual+ report. Try again later.');
        return;
      }

      // Pull the heavyweight report only now that it is ready.
      await this.store.jobManager.apiFetchJob(this.jobId);
      if (!this.job?.publicSafetyDualPlusReport) {
        notifyError(this, 'Could not generate the Dual+ report. Try again later.');
        return;
      }

      notifySuccess(this, 'Dual+ report generated.');
      this.dispatchDualPlusEditor(this.job);
    } finally {
      this.setDualPlusReportGenerating(false);
    }
  };

  private dispatchDualPlusEditor = (job: JobModel) => {
    this.dispatch('openEditorWindow', {
      data: job.publicSafetyDualPlusReport,
      fileName: `${job.title || 'Dual+'}-dual-plus-report.pdf`,
      jobId: this.jobId,
      mode: 'dualPlusReport',
      editorMode: 'collaboration',
    });
  };

  private fetchDualPlusStatus = async (): Promise<DualPlusReportStatus | null> => {
    if (!this.jobId) return null;
    const [res, err] = await this.store.api.runQuery('getJob_checkDualPlusStatus', { id: this.jobId });
    if (err) return null;
    return res?.getJob?.publicSafetyDualPlusReportStatus ?? null;
  };

  // Polls until generation leaves the Generating state; returns the terminal status
  // (Complete/Failed) or null on timeout. Ceiling ~10min covers the worker's 300s
  // Lambda timeout plus Step Functions retries.
  private pollDualPlusStatus = async (): Promise<DualPlusReportStatus | null> => {
    const MAX_POLLS = 120;

    for (let i = 0; i < MAX_POLLS; i++) {
      if (this.dualPlusPollIntervalMs > 0)
        await new Promise((resolve) => setTimeout(resolve, this.dualPlusPollIntervalMs));

      const status = await this.fetchDualPlusStatus();
      // null = transient query error; keep polling rather than aborting a live run.
      if (status && status !== DualPlusReportStatus.Generating)
        return status;
    }

    return null;
  };

  openPublicSafetyJobEditorWindow = async (jobId: string, fileName: string, mode: 'notes' | 'report') => {
    this.invoke('enterPause');

    await this.store.jobManager.apiFetchJob(jobId);
    const job = this.store.maybeGetJob(jobId);

    const dataByMode: Record<string, string | null | undefined> = {
      notes: job?.publicSafetyNotes,
      report: job?.publicSafetyReport,
    };

    this.dispatch('openEditorWindow', {
      data: dataByMode[mode],
      fileName,
      jobId,
      mode,
      editorMode: 'collaboration',
      readOnly: true,
    });
  };

  async updateJob(args: UpdateJobInput): AsyncResult<JobModel> {
    return this.store.apiUpdateJob(args);
  }

  unregisterListeners() {
    this.store.orientation.dispose();
    this.store.playerTutorialWindow.unlisten(this.playerTutorialWindowListener);
  }

  unmounted() {
    TRACE(this, 'unmounted()');

    this.emit('Unmounted');
    this.setUnmounted();
    this.unregisterListeners();
    this.lockOrientation = false;
    this.mobxDisposers.forEach(d => d());
    this.mobxDisposers = [];
  }

  reset() {
    TRACE(this, `reset()`);

    this.momentEditor.reset();
    this.momentDirector.reset();

    this.resetPlayState();
    this.playerLiveAdapter.reset();
    this.isReplay = false;
    this.adsAdapter.reset();

    this.mediaStreamMonitor.reset();
    this.playerCaption.reset();
  }

  resetPlayState() {
    this.playCount = 0;
    this.playInvokeCount = 0;
    this.isEnded = false;
    this.chrome.endControlAction();
  }

  private setMounted() {
    this.isMounted = true;
  }

  private setUnmounted() {
    this.isMounted = false;
  }

  invokeHandler = (type: string, payload?: PlayerInvokePayload | null) =>
    (_evt: unknown) => this.invoke(type, payload);

  invoke = async (type: string, payload?: PlayerInvokePayload | null) => {

    TRACE(this, `Invoke`, type, payload);

    const { adapter, activeAdapter } = this;

    // Log the last action made by the user for visual feedback
    this.toggleLastActionFeedback(type);

    switch (type) {

      case 'autoplay':
        if (this.isLiveStream)
          this.playerLiveAdapter.invoke('autoplay');
        else {
          // TODO: verify this still behaves the same as when autoplay was handled inside the adapter
          adapter.invoke('setAutoplay', { autoplay: true });
          adapter.invoke('play');
        }
        break;

      case 'play':
        this.play(payload ?? {});
        break;

      case 'pause':
        if (this.isLiveStream)
          this.playerLiveAdapter.invoke('pause');
        else
          activeAdapter.invoke('pause');
        break;

      case 'disableAutoplay':
        activeAdapter.invoke('setAutoplay', { autoplay: false });
        break;

      case 'stop':
        this.invoke('pause');
        this.resetPlayState();
        break;

      case 'end':
        this.invoke('pause');
        this.resetPlayState();
        this.isEnded = true;
        break;

      case 'enterPause':
        if (this.isPlaying)
          this.wasPlayingBeforeEnterPause = true;

        this.isPauseEntered = true;
        activeAdapter.invoke('pause');
        break;

      case 'exitPause':
        if (this.wasPlayingBeforeEnterPause)
          activeAdapter.invoke('play', payload);

        this.isPauseEntered = false;
        this.wasPlayingBeforeEnterPause = false;
        break;

      case 'togglePlay':
        if (this.isPlaying)
          this.invoke('pause');
        else
          this.invoke('play');
        break;

      case 'toggleRealTimeMode':
        this.enterRealTime();
        break;

      case 'setPlaybackRate':
        if (this.isLiveStream)
          this.playerLiveAdapter.invoke('setPlaybackRate', payload);
        else
          activeAdapter.invoke('setPlaybackRate', payload);
        break;

      case 'mute':
        this.isMuted = true;
        this.volumeBeforeMute = this.volume;
        activeAdapter.invoke('setVolume', { volume: 0 });
        break;

      case 'unmute':
        activeAdapter.invoke('setVolume', { volume: this.volumeBeforeMute || 0.5 });

        this.isMuted = false;
        this.volumeBeforeMute = null;
        break;

      case 'setVolume':
        activeAdapter.invoke('setVolume', { volume: payload?.volume });
        this.isMuted = false;
        break;

      case 'toggleMute':
        if (this.isMuted || this.volume === 0)
          this.invoke('unmute');
        else
          this.invoke('mute');
        break;

      case 'seek':
        if (payload?.time !== undefined)
          this.seek(payload.time, true);
        break;

      case 'seekStart':
        this.isSeekDragging = true;
        if (payload?.time !== undefined)
          this.seek(payload.time, true);
        break;

      case 'seekEnd':
        if (payload?.time !== undefined)
          this.seek(payload.time, false);
        this.isSeekDragging = false;
        break;

      case 'seekStepForward':
        this.seekStepForward();
        break;
      case 'seekStepBackward':
        this.seekStepBackward();
        break;

      case 'seekToEnd':
        if (this.hasDuration)
          this.seek(this.duration);
        break;

      case 'hoverSeek':
        if (payload?.time !== undefined)
          this.hoverSeekTime = payload.time;
        break;

      case 'hoverSeekStart':
        this.isHoverSeeking = true;
        if (payload?.time !== undefined)
          this.hoverSeekTime = payload.time;
        break;

      case 'hoverSeekEnd':
        this.isHoverSeeking = false;
        // state.hoverSeekTime = 0;
        break;

      case 'jumpToMoment': {
        const moment = payload?.moment;
        if (!moment) break;
        this.seek(getJumpToMomentTime(this, moment.startTime));

        if (payload?.setAsPrincipal)
          this.principalMoment = moment;
        else
          this.principalMoment = null;
      } break;

      case 'jumpToComment':
        if (payload?.comment?.videoTime != null)
          this.seek(getJumpToCommentTime(this, payload.comment.videoTime));
        break;

      case 'jumpToReaction':
        if (payload?.reaction?.videoTime != null)
          this.seek(getJumpToReactionTime(this, payload.reaction.videoTime));
        break;

      case 'jumpToCommentOrReaction':
        if (payload?.target)
          this.seek(getJumpToCommentOrReactionTime(
            this, payload.target, this.job?.source.liveCliprHLS));
        break;

      case 'enableCaptions':
        this.playerCaption.enableCaptions();
        break;

      case 'disableCaptions':
        this.playerCaption.disableCaptions();
        break;

      case 'reattach':
        this.adapter.reattachDOMVideo();
        break;

      case 'toggleCaptions':
        this.playerCaption.toggleCaptions();
        break;

      case 'exitRealTime':
        this.exitRealTime();
        break;
      case 'enterRealTime':
        this.enterRealTime();
        break;
      case 'updateRealTime':
        this.updateRealTime();
        break;

      case 'replay':
        this.applyReplayInitialSeek();
        this.invoke('play');
        break;

      case 'toggleFullscreen':

        this.lockOrientation = true;

        try {
          await this.store.fullscreen.toggleFullscreen(
            this.componentRef.node);
        } catch {
          this.lockOrientation = false;
        }
        break;

      case 'enterFullscreen':
        await this.store.fullscreen.ensureFullscreen(
          this.componentRef.node);
        break;

      case 'exitFullscreen':
        await this.store.fullscreen.ensureNotFullscreen();
        break;

      // added to work with trainer page
      case 'clearMomentStub':
        this.momentEditor.exitEditMode();
        break;

      case 'startMomentStub':
        // TODO: introduce a command policy system — guard patterns and visibility overrides are duplicated across invoke() and getComponentPolicy()
        if (this.isEnrichmentProcessing || this.job?.status === 'InProgress')
          return;
        this.momentEditor.enterEditMode();
        break;

      case 'confirmMomentStub':
        if (this.job?.status === 'InProgress' || !this.isEditMode)
          return;
        this.momentEditor.editConfirmMoment(this.openMomentWindowOptions);
        break;

      default:
        break;
    }
    // These events are emitted from the adapterListener as well
    // TODO: verify if this emit is still needed given adapterListener also emits these events
    if (!['play', 'pause'].includes(type)) {
      this.emit(type, payload);
    }

    // emit an aggregate seekChange event
    switch (type) {
      case 'seek':
      case 'seekStart':
      case 'seekEnd':
      case 'seekStepForward':
      case 'seekStepBackward':
      case 'jumpToMoment':
        this.emit('seekChange', { time: this.currentSeekTime });
        break;
    }
  }

  async handleFullscreenChange() {

    if (!this.componentRef.node)
      return;

    if (this.isFullscreen && this.lockOrientation) {
      // await this.store.orientation.lockPortrait();
      // this.store.orientation.unlock();
      await this.store.orientation.lockLandscape();
    }

    if (!this.isFullscreen)
      this.store.orientation.unlock();

    this.lockOrientation = false;
  }

  /**
   * MobX reaction handler for `principalMomentStatus`.
   */
  protected handlePrincipalMomentStatusReaction = (curr: PlayerPrincipalMomentStatus, _prev: PlayerPrincipalMomentStatus) => {

    if (curr === PlayerPrincipalMomentStatus.PlaybackEnded) {
      this.invoke('pause');
      this.principalMoment = null;
    }
  }

  enterEditMode() {
    return this.momentEditor.enterEditMode();
  }

  exitEditMode() {
    return this.momentEditor.exitEditMode();
  }

  toggleEditMode() {
    return this.momentEditor.toggleEditMode();
  }

  momentHasBookmark(moment?: MomentModel | null): boolean {
    return momentHasBookmarkHelper(moment);
  }

  openBookmarkWindow(moment?: MomentModel | null) {
    if (!moment)
      return false;

    const window = this.store.bookmarkWindow;
    const listener = (msg: Message<BookmarkWindowState>) => {
      switch (msg.type) {
        case 'open':
          this.invoke('enterPause');
          break;
        case 'close':
          window.unlisten(listener);
          // this.invoke('exitPause');
          break;
      }
    }

    window.listen(listener);
    window.openMoment(moment.id, moment.jobId);

    return true;
  }

  // #region Event handlers
  // -------
  handleTimelineClick = (evt: React.MouseEvent) => {
    this.seek(
      this.getTimeFromEvent(evt, this.progressBarRef));
  }

  handleLoginButtonClick = () => {
    this.emit('requestLogin');
  }

  handleLogoutButtonClick = () => {
    this.emit('requestLogout');
  }

  handleHelpButtonClick = () => {
    this.emit('openTutorialWindow');
  }
  // #endregion

  async copyIFrameEmbedCode() {
    const { jobId } = this;
    const { ui } = this.store;
    if (!jobId)
      return;

    const { job } = this;
    const code = getPlayerWidgetIFrameCode({
      jobId,
      time: (job?.isLiveStreamWaiting || job?.isLiveStreaming) ? 'none' : this.time,
      theme: ui.theme,
      showIndex: this.showIndex,
      showComments: this.showComments,
      showTranscript: this.showTranscript,
      showProfile: this.showProfile,
      showTopicTags: this.showTopicTags,
      showEmbedIcon: this.showEmbedIcon,
      showHelp: this.showHelp,
      disableReactions: this.disableReactions,
      disableComments: this.disableComments,
      disableTranscript: this.disableTranscript,
      disableIndex: this.disableIndex,
      allowFullscreen: this.allowFullscreen,
      allowShare: this.allowShare,
      allowDownload: this.allowDownload,
      autoplay: this.autoplay,
      teamId: this.teamId,
      customRedirect: this.customRedirectUrl,
    });

    if (!code)
      return;

    await navigator.clipboard?.writeText(code);
    notifySuccess(this, 'Embed code copied to clipboard!');
  }

  applyDirectorInitialSeek(): boolean {
    const seekTime = getDirectorInitialSeekTime(this.momentDirectorMoments || []);
    if (seekTime !== null) {
      this.seek(seekTime);
      return true;
    }
    return false;
  }

  applyReplayInitialSeek(): boolean {
    const dirApp = this.applyDirectorInitialSeek();
    if (!dirApp)
      this.seek(0);
    return true;
  }

  isComponentVisible(name: PlayerComponentName) {
    const policy = this.getComponentPolicy(name);
    return policy.visibility === ComponentVisibility.Visible;
  }

  isComponentReadOnly(name: PlayerComponentName) {
    const policy = this.getComponentPolicy(name);
    return !!policy.isReadOnly;
  }

  isComponentDisabled(name: PlayerComponentName) {
    if (this.getComponentPolicy) {
      const policy = this.getComponentPolicy(name);
      return !!policy.isDisabled;
    }

    return true;
  }

  getComponentPolicy(name: PlayerComponentName): IComponentPolicy {
    const isAdMode = this.isAdPlaybackMode;
    const isMobile = this.isMobile;

    const policy: IComponentPolicy = {
      visibility: ComponentVisibility.Visible,
      isReadOnly: false,
      isDisabled: false
    }

    const hide = () => {
      policy.visibility = ComponentVisibility.Hidden;
    }
    const readOnly = () => {
      policy.isReadOnly = true;
    }
    const disable = () => {
      policy.isDisabled = true;
    }

    switch (name) {
      case PlayerComponentName.PlayPauseButton:
        break;

      case PlayerComponentName.SeekBackButton:
        if (isAdMode || !this.isSeekable)
          hide();
        break;

      case PlayerComponentName.SeekNextButton:
        if (isAdMode || !this.isSeekable)
          hide();
        break;

      case PlayerComponentName.PlaybackRateButton:
        // TODO: add an `allowPlaybackRateChange` flag to the adapter interface — google ima sdk doesn't support it
        if (isAdMode || !this.isSeekable)
          hide();
        break;

      case PlayerComponentName.VolumeButton:
        // TODO: add an `allowVolumeChange` flag to the adapter interface — google ima sdk throws on volume calls in ad mode
        if (isAdMode || isMobile)
          hide();
        break;

      case PlayerComponentName.ClosedCaptionsButton:
        const hasCaptions = this.playerCaption.hasCaptions;
        const hideCaptionsForVOD = !this.isLiveStream && !this.job?.isTranscriptDone;
        if (isAdMode || !hasCaptions || hideCaptionsForVOD || this.playerLiveAdapter.isHlsStreamCompleted)
          hide();
        break;

      case PlayerComponentName.AudioTrackButton:
        if (isAdMode || !this.hasMultipleAudioTracks || this.playerLiveAdapter.isHlsStreamCompleted)
          hide();
        break;

      case PlayerComponentName.ReactionButton:
        if (isAdMode || this.showReactionButton === false)
          hide();
        break;

      case PlayerComponentName.ProgressBar:
        if (isAdMode)
          readOnly();
        break;

      case PlayerComponentName.MarkerBar:
        if (isAdMode)
          hide();
        break;

      case PlayerComponentName.ExplorerBar:
        if (isAdMode)
          hide();
        break;

      case PlayerComponentName.ProgressBarMoments:
        if (isAdMode)
          hide();
        break;

      case PlayerComponentName.AdsBar:
        if (!isAdMode)
          hide();
        break;

      case PlayerComponentName.CommentsSection: {
        if (isAdMode)
          disable();
        break;
      }

      case PlayerComponentName.TranscriptsSection: {
        if (isAdMode)
          disable();
        break;
      }

      case PlayerComponentName.IndexSection: {
        if (isAdMode)
          disable();
        break;
      }

      case PlayerComponentName.Toolbar: {
        if (isAdMode)
          disable();
        break;
      }

      case PlayerComponentName.AddMomentButton: {
        if (
          isAdMode ||
          this.job?.status === 'InProgress' ||
          this.showAddMomentButton === false)
          hide();

        // see command policy TODO in invoke() — showAddMomentButton prop overrides the default visibility logic
        const defaultShow = (
          this.isWidgetMode &&
          this.job?.hasPermission('EditUserMoment') &&
          this.showAddClip &&
          !this.isMobile);

        if (!defaultShow && this.showAddMomentButton !== true)
          hide();

        break;
      }

      case PlayerComponentName.MomentEditorGroup: {
        if (isAdMode)
          hide();
        break;
      }
    }

    return policy;
  }

  // #region Queries / Mutations
  async addReaction(name: InputPlayerReactionName)
    : AsyncResult<Reaction> {

    const [reaction, err] = await addReactionHelper(this, name);

    this.invoke('exitPause');
    if (err || !reaction)
      return [null, err];

    this.broadcast('reactionAdded', { reactionName: name, job: this.job });
    return [reaction];
  }

  throttledAddReaction = throttle((name) => this.addReaction(name), ADD_REACTION_THROTTLE_TIME, {
    leading: true,
    trailing: false
  })
  // #endregion

  shareVideo(moment?: MomentModel) {
    this.invoke('enterPause');

    const { shareVideoWindow: window, ui } = this.store;
    const listener = (msg: Message<ShareVideoWindowState>) => {
      switch (msg.type) {
        case 'close':
          window.unlisten(listener);
          this.invoke('exitPause');
          break;
      }
    }
    window.listen(listener);

    const args: ShareWindowProps = {
      mode: ShareWindowMode.Social,
      job: this.job,
      teamId: this.teamId,
      sharedFromLocation: SharedFrom.PlayerPage,
      moment,
      theme: ui.theme,
      showIndex: this.showIndex,
      showComments: this.showComments,
      showTranscript: this.showTranscript,
      showProfile: this.showProfile,
      showTopicTags: this.showTopicTags,
      showEmbedIcon: this.showEmbedIcon,
      showHelp: this.showHelp,
      disableReactions: this.disableReactions,
      disableComments: this.disableComments,
      disableTranscript: this.disableTranscript,
      disableIndex: this.disableIndex,
      allowFullscreen: this.allowFullscreen,
      allowShare: this.allowShare,
      allowDownload: this.allowDownload,
      autoplay: this.autoplay,
      time: this.time,
      customRedirectUrl: this.customRedirectUrl
    };
    window.open(args);
  }

  shareMeetingAssets() {
    if (!this.job) return;
    this.invoke('enterPause');

    const { shareMeetingAssetsWindow: window } = this.store;
    const listener = (msg: Message<ShareMeetingAssetsWindowState>) => {
      switch (msg.type) {
        case 'close':
          window.unlisten(listener);
          this.invoke('exitPause');
          break;
      }
    };
    window.listen(listener);

    window.open({ job: this.job, allowShare: this.allowShare });
  }

  handleOrientationChange = () => {
    const orientationAction = getOrientationAction(this);
    if (orientationAction)
      this.invoke(orientationAction);
  }

  // #region Query methods
  // -------
  getTimeFromRatio(ratio: number) {

    if (this.isLiveStream)
      return this.playerLiveAdapter.getTimeFromRatio(ratio);

    if (this.hasDuration)
      return (ratio * this.duration) || 0;
    return 0;
  }

  getTimeFromEvent(evt: PointerEventLike, containerRef?: Maybe<React.RefObject<HTMLElement> | RefProxy<HTMLElement>>) {

    if (!containerRef || !containerRef.current)
      return 0;

    const container = containerRef.current;
    const offset = getEventOffset(evt, container);

    return this.getTimeFromRatio(
      clamp(offset.x / container.offsetWidth, 0, 1))
  }

  getTimeRatio(time: number) {

    if (this.isLiveStream)
      return this.playerLiveAdapter.getTimeRatio(time);

    if (this.hasDuration)
      return (time / this.duration) || 0;
    return 0;
  }
  // #endregion

  private playerTutorialWindowListener = (msg: Message<PlayerTutorialWindowState>) => {
    const tutorialState = handleTutorialWindowMessage(msg);
    if (tutorialState !== null)
      this.tutorialMode = tutorialState;
  }

  // #region API / seek
  seek = (time: number, debounce = false) => {

    if (!this.isSeekable)
      return;

    this.currentSeekTime = time;
    if (debounce) {
      this.adapterSeekDebounced(time);
      return;
    }

    // debounce is falsy, so cancel any previously debounced functions
    // otherwise the seek call might be triggered twice (one from the call below and one from a previously debounced call, if any)
    this.adapterSeekDebounced.cancel();
    this.adapterSeek(time);

    // clear flags
    this.principalMoment = null;
  }

  seekStep = (delta: number) => {
    if (!this.hasDuration)
      return;

    const time = clamp((this.currentSeekTime || this.currentTime) + delta, 0, this.duration);
    this.seek(time, false);
  }

  seekStepForward() {
    if (this.isLiveStream && this.isRealTime)
      return;

    return this.seekStep(10);
  }

  seekStepBackward() {
    return this.seekStep(-10);
  }

  adapterSeek = (time: number) => {

    if (this.adapter.currentTime === time) {
      // This is fix for a bug on some videos where the video element is stuck on 'seeking'
      // thus at load if time === 0 it will be equal to currentTime and it won't set it again

      // NOTE: this was initially fixed on https://github.com/cliprai/clipr/pull/721
      // and the check was put on PlayerAdapterState.seek
      // however this was causing issues when having a `time` parameter set on a widget with a value of 0
      // which was causing an automatic seek which in turn was discarded because `currentTime` was also 0
      // which ultimately caused the `playheadTime` to remain 0 because `isSeekRequested` was true and no `seeked`
      // event came back from PlayerAdapter
      return;
    }

    if (this.isLiveStream) {
      this.playerLiveAdapter.invoke('seek', { time });
      this.isSeekRequested = true;
      return;
    }

    this.adapter.invoke('seek', { time });

    this.isSeekRequested = true;

    const { trainerVideoPage } = this.store;
    trainerVideoPage.timeline.playhead.scrollIntoView();
  }

  adapterSeekDebounced = debounce((time: number) => this.adapterSeek(time),
    SEEK_DEBOUNCE_WAIT);

  enterRealTime = () => {
    if (!this.isLiveStream)
      return;

    this.playerLiveAdapter.invoke('enterRealTime');
  }

  exitRealTime = () => {
    if (!this.isLiveStream)
      return;

    this.playerLiveAdapter.invoke('exitRealTime');
  }

  updateRealTime = () => {
    if (!this.isLiveStream)
      return;

    this.playerLiveAdapter.invoke('updateRealTime');
  }
  // #endregion

  // #region Listeners
  // -------
  playheadListener = (msg: Message<PlayheadState>) => {

    switch (msg.type) {
      case 'seek':
      case 'seekStart':
      case 'seekEnd':
        this.invoke(msg.type, msg.payload);
        break;

      default: break;
    }
  }

  progressBarListener = (msg: Message<PlayerProgressBarState>) => {

    switch (msg.type) {
      case 'seek':
      case 'seekStart':
      case 'seekEnd':
        this.invoke(msg.type, msg.payload);
        break;

      default: break;
    }
  }

  handleLivePlaybackCompleted = async () => {
    const { job } = this;
    if (!job)
      return;

    const [res, err] = await this.store.apiFetchJob(job.id);

    if (!res && err)
      console.warn('job could not be fetched:', err);

    if (job.isMediaDone || job.isMediaFailed) {
      // console.log('media ready - reattach video for VOD');
      this.reset();
      this.adapter.reattachDOMVideo(); // after stream playback is completed, reload media if VOD is ready
    }

    if (res)
      await res.apiFetchReactions();
  }

  adapterListener = async (msg: Message<PlayerAdapterState>) => {

    // if (msg.type !== 'timeupdate')
    //   TRACE(this, `Adapter message`, msg.type, msg.payload);

    switch (msg.type) {
      case 'play':

        // a safe measure to make sure at first play the live is seeked to real time
        if (this.isLiveStream && this.playCount === 0) {
          // console.log('at first play', {
          //   seekableStart: this.playerLiveAdapter.liveSeekableStart,
          //   liveSeekableEnd: this.playerLiveAdapter.appliedLiveCurrentTime,
          //   seekableEnd: this.playerLiveAdapter.seekableEnd,
          //   currentTime: this.playerLiveAdapter.currentTime
          // });
          this.playerLiveAdapter.seekToLiveEdge();
        }

        this.playCount++;
        this.isEnded = false;
        break;

      case 'playing':
        this.isEnded = false;
        break;

      case 'pause':
        break;

      case 'seeking':
        this.isEnded = false;
        this.emit('seeking');
        break;

      case 'seeked':
        this.isEnded = false;
        this.isSeekRequested = false;
        if (!this.isSeekDragging)
          this.currentSeekTime = null;

        this.emit('seeked');
        break;

      case 'ended':
        if (this.isLiveStream) {
          // console.log('playback completed - media');
          this.playerLiveAdapter.invoke('playbackCompleted');
        }

        this.adsAdapter.signalVideoEnded();
        this.isEnded = true;
        break;

      case 'endOfStream':
        if (this.isLiveStream) {
          // console.log('EOS');
          this.playerLiveAdapter.setStreamEnded();
        }
        break;

      case 'videoDetached':
        this.reset();
        break;

      case 'loadedmetadata':
        if (this.adapter.videoElement) // the textStream might be initiated or added later during the stream
          this.adapter.videoElement.textTracks.onaddtrack = this.playerCaption.initMediaCaptions;
        break;
      case 'loadeddata':
        this.playerCaption.initMediaCaptions(); // the textStream might be initiated or added later during the stream
        break;

      default: break;
    }

    this.emit(msg.type, msg.payload);
  }
  // #endregion

  adsAdapterListener = (msg: Message<PlayerAdsAdapterState>) => {
    TRACE(this, `AdsAdapter message`, msg.type, msg.payload);

    switch (msg.type) {
      case PlayerAdsAdapterMessageType.RequestBreak:
        this.adapter.invoke('pause');
        break;

      case PlayerAdsAdapterMessageType.RequestResume:
        if (!this.isEnded)
          this.adapter.invoke('play');
        break;
    }
  }

  playerLiveAdapterListener = (msg: Message<PlayerLiveAdapter>) => {
    switch (msg.type) {
      case 'playbackCompleted':
        this.handleLivePlaybackCompleted();
        break;
    }
  }

  private toggleLastActionFeedback(action: string) {
    this.lastUserAction = action;
    this.showLastActionFeedback = true;

    this.debouncedFeedbackToggle();
  }
  private debouncedFeedbackToggle = debounce(() => this.showLastActionFeedback = false, 350);

  handleVolumeSliderChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    this.adapter.invoke('setVolume', { volume: parseFloat(evt.target.value) });
  }

  applyTimeParam(time: number | null) {

    if (!isFiniteNumber(time))
      return false;

    if (this.hasDuration && time > this.duration)
      return false;

    this.invoke('seek', { time });
    return true;
  }

  setAdTagUrl(url: string) {
    this.adsAdapter.setAdTagUrl(url);
  }

  async loadAdTagUrl(reqOpts?: ApiRequestOptions) {
    TRACE(this, `Loading ads from team settings`);

    const { store, teamId, job } = this;
    if (!teamId) {
      TRACE(this, `Cannot load ads because no 'teamId' is available`);
      return;
    }

    // TODO: replace triple-check with a single isLiveJob predicate on JobModel
    if (job?.liveStream || job?.media.liveStreamUrl || job?.source.type === 'Live') {
      TRACE(this, `Ads will not be loaded because the job is a live one`);
      return;
    }

    const [res, err] = await store.api.runQuery('getTeam_getAds', { id: teamId }, reqOpts);
    if (err) {
      TRACE(this, `An error occurred while loading ads from team settings:`, err);
      return [null, err];
    }

    // we do nothing in case there's an error with getting the ads
    const adTagUrls = res?.getTeam.adTagUrls;
    if (!Array.isArray(adTagUrls)) {
      TRACE(this, `'getTeam_getAds' query for ads was loaded but the returned 'adTagUrls' object is not an array`, adTagUrls);
      return;
    }

    const adTagUrl = randomArrayElement(adTagUrls);
    if (!isNonEmptyString(adTagUrl)) {
      TRACE(this, `'getTeam_getAds' query for ads was loaded but no valid URL has been randomly selected from 'adTagUrls' object`, adTagUrl);
      return;
    }

    this.setAdTagUrl(adTagUrl);
  }

  async start() {
    TRACE(this, `start()`);

    const autoplay = this.autoplay ?? true;
    const { adsAdapter } = this;
    const { autoplayPolicy } = Config.player;

    // in an ideal world, the bastard google ima sdk would tell you if autoplay will work or not
    // in a semi-ideal world, even if the sdk doesn't tell you that, at least it will let you play
    // the ad again on user action after a failed autoplay attempt
    // but obviously we live in a world in which the "best" programmers in the world don't tell you
    // if autoplay will work and also they won't let you play the ad again on user action after a failed autoplay attempt
    // as always, if you want to do something right, you have to do it yourself

    // we can find out if autoplay is allowed for non-muted and muted scenarios in parallel

    TRACE(this, `Detecting if autoplay is allowed`);
    const [canAutoplay] = await this.store.autoplay.canAutoplayVideo({ timeout: 2000 });
    TRACE(this, `Detected that autoplay is ${canAutoplay ? 'allowed' : 'restricted'}`);

    adsAdapter.setAdWillAutoplay(canAutoplay ?? false);
    adsAdapter.setAdWillAutoplayMuted(false);

    const useAutoplay = (() => {
      switch (autoplayPolicy) {
        case ConfigAutoplayPolicy.Disabled:
          return false;
        case ConfigAutoplayPolicy.Standard:
          return canAutoplay && autoplay;
        case ConfigAutoplayPolicy.Force:
          return true;
      }
    })();

    TRACE(this, `Loading the ads adapter`);

    const [adsLoaded, adsLoadErr] = await adsAdapter.load();
    if (!adsLoaded || adsLoadErr)
      TRACE(this, `Ads adapter loading failed`);
    else
      TRACE(this, `Ads adapter loading succeeded`);

    if (useAutoplay) {

      TRACE(this, `Will try to autoplay because the browser allows it and it was requested by the current configuration`);

      const playAds = adsAdapter.adTagUrl && adsLoaded && !adsLoadErr;
      let playVideo = !playAds;

      if (playAds) {

        TRACE(this, `Autoplay the ads because we have an ad tag URL set and the ads adapter load was completed successfully`);

        this.adapter.pause();

        const [adsStarted, adsStartErr] = adsAdapter.start();
        if (!adsStarted || adsStartErr) {
          TRACE(this, `Autoplaying the ads failed, will proceed to autoplay the main video`);
          playVideo = true;
        } else {
          TRACE(this, `Autoplaying the ads succeeded`);
          // it should already be false, but set it for safety
          playVideo = false;
        }
      }

      if (playVideo) {
        // the play promise might hang indefinitely if the source is messed up or timing out
        // thus we wait on the result only internally for logging
        // but the output of the `start` function won't wait on the start result
        // in the future this should be handled in a more predictable way,
        // maybe by waiting for the first non-error event on the player

        TRACE(this, `Autoplay the video`);

        (async () => {
          const [, err] = await this.adapter.play();

          if (err) {
            TRACE(this, `Autoplay failed`);
            return [null, err];
          }

          TRACE(this, `Autoplay success`);
        })();

        TRACE(this, `Autoplay call completed (not waiting on it)`);
      }
    }
  }

  async play(params: PlayParams) {

    if (this.isEnded)
      this.isReplay = true;

    const { isReplay } = this;
    const isFirst = this.isStopped && !this.isEnded;

    this.isEnded = false;
    this.playInvokeCount++;

    if (this.isLiveStream) {
      this.playerLiveAdapter.invoke('play', params);
    }
    else {
      if (isFirst && !isReplay && this.adsAdapter.adTagUrl) {
        this.adsAdapter.start();
      }
      else {
        await this.activeAdapter.invoke('play');
      }
    }
  }
}
